import {
  FlexBox,
  JoonColorExpanded,
  JoonUIColor,
  SPACING,
  Typography,
} from "@joonapp/web-shared"
import { useLayoutEffect } from "react"

import CareMarketingCard from "./CareMarketingCard"
import CareTeamCard from "./CareTeamCard"
import TherapistInviteItem from "./therapists/TherapistInviteItem"
import { useLinkTherapistModalStore } from "./therapists/useLinkTherapistModalStore"
import useCareTeam from "./useCareTeam"
import Card from "../../components/card/Card"
import PageContentWrapper from "../../components/pageContentWrapper/PageContentWrapper"
import PageWrapper from "../../components/pageWrapper/PageWrapper"
import useDocumentTitle from "../../hooks/useDocumentTitle"
import useIsCoachingAvailable from "../../hooks/useIsCoachingAvailable"
import { useTherapistInvitesQuery } from "../../networking/queries"
import { requireAuth } from "../../util/auth"
import { useRouter } from "../../util/router"

const CarePage = () => {
  useDocumentTitle("Joon - Care")
  const { onOpen: openLinkTherapistModal } = useLinkTherapistModalStore()
  const {
    careTeam,
    isLoading: isLoadingCareTeam,
    hasCoach,
    hasTherapist,
    firstCoach,
  } = useCareTeam()
  const router = useRouter()
  const isCoachingAvailable = useIsCoachingAvailable()
  const { data: therapistInvites, isLoading: isLoadingTherapistInvites } =
    useTherapistInvitesQuery()
  const hasTherapistInvites = therapistInvites && therapistInvites.length > 0

  const isLoading = isLoadingCareTeam || isLoadingTherapistInvites

  useLayoutEffect(() => {
    if (isLoading || hasTherapist || hasTherapistInvites) return

    // Nothing to show but the coaching upsell — send them home instead when they
    // can't buy a coach, since the nav entry for this tab is hidden for them too.
    if (!hasCoach) router.push(isCoachingAvailable ? "/coach" : "/me")
    else router.push(`/coach/${firstCoach?.user.id}`)
  }, [
    careTeam,
    isLoading,
    hasTherapist,
    hasCoach,
    firstCoach,
    router,
    hasTherapistInvites,
    isCoachingAvailable,
  ])

  if (isLoading) return <></>

  return (
    <PageWrapper
      style={{
        background: JoonColorExpanded.yellow100,
        paddingBottom: "0px",
      }}
    >
      <PageContentWrapper mobileGutter>
        <FlexBox
          direction="column"
          align="center"
          wrap={false}
          style={{
            position: "relative",
            padding: `${SPACING.space9} ${SPACING.space2}`,
            width: "min(600px, 100%)",
          }}
          gap={SPACING.space9}
        >
          <Typography variant="h1" textAlign="left">
            Your care team
          </Typography>
          <FlexBox
            direction="column"
            align="flex-start"
            justify="flex-start"
            gap={SPACING.space2}
            fullWidth
          >
            {careTeam?.map((profile) => (
              <CareTeamCard key={profile.id} profile={profile} />
            ))}
            {therapistInvites &&
              therapistInvites.length > 0 &&
              therapistInvites.map((inviteGroup, index) => (
                <TherapistInviteItem
                  key={index}
                  invitationGroup={inviteGroup}
                />
              ))}
            <Card
              onClick={() => openLinkTherapistModal(false)}
              title={
                <FlexBox
                  direction="row"
                  align="center"
                  justify="flex-start"
                  gap={SPACING.space2}
                  wrap={false}
                >
                  <div
                    style={{
                      minWidth: "32px",
                      minHeight: "32px",
                      borderRadius: "50%",
                      background: JoonUIColor.background.lightGray,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="h2" color={JoonUIColor.icon.neutral}>
                      +
                    </Typography>
                  </div>
                  <Typography
                    variant="bodySmallBold"
                    color={JoonUIColor.text.primary}
                  >
                    Invite another therapist
                  </Typography>
                </FlexBox>
              }
              style={{
                padding: SPACING.space3,
                paddingRight: "0px",
                width: "99%",
                position: "relative",
                border: "none",
              }}
            />
            {!hasCoach && isCoachingAvailable && <CareMarketingCard />}
          </FlexBox>
        </FlexBox>
      </PageContentWrapper>
    </PageWrapper>
  )
}

const CarePageWithAuth = requireAuth(CarePage)
export default CarePageWithAuth
